import { STUDENT_INPERSON } from '../../constants'
import { useFormikContext } from 'formik'
import {
  StudentUpdateFormValues,
  ViewMode,
  FormikOnlyValues,
  ConfigUtils,
  ModuleProgress,
} from 'types'
import moment from 'moment'
import { Box, Flex } from 'theme-ui'
import { Label } from 'components/Form'
import { Chip, Text, Tooltip } from 'components'
import {
  LockCourseBtn,
  UnlockCourseBtn,
} from 'components/UserForm/AccountActions'
import { mapProgressFromObj, isV2OrHigher, calculateV2Progress } from 'utils/dc'

const APP_DELETED_TOOLTIP =
  'This person requested deletion of their app account. Their app login and app-specific data (devices, sessions, saved library items) were removed; their learning history and records are retained.'

export type StatusSectionFields =
  | 'status'
  | 'instructionDate'
  | 'userType'
  | 'countryCode'
  | 'id'
  | 'teachingOrg'
  | 'dcProgress'
  | 'dcVersion'
  | 'moduleProgress'
  | 'hasAppAccess'
export type StatusSectionFormValues = Pick<
  StudentUpdateFormValues,
  StatusSectionFields
>

// Format module progress as single line: "Module 1: X%, Module 2: Y%, ..."
const formatModuleProgressLine = (
  moduleProgress?: ModuleProgress[]
): string => {
  if (!moduleProgress || moduleProgress.length === 0) return ''
  return moduleProgress
    .map(({ module, percentage }) => `Module ${module}: ${percentage}%`)
    .join(', ')
}

export const StatusSection = ({
  isSupport,
}: {
  isSupport?: boolean
  viewMode?: ViewMode
  configUtils: ConfigUtils
  isDisabledField: (fieldName: keyof StatusSectionFormValues) => boolean
  shouldShowError?: (fieldName: keyof StatusSectionFormValues) => boolean
  startTabIndex: number
}) => {
  const { values, initialValues } = useFormikContext<
    StatusSectionFormValues & FormikOnlyValues
  >()
  const isUserLocked = values.status === 'locked'
  const isUserUnlocked = values.status === 'unlocked'
  const disableLockUnlockButton = moment().isBefore(values.instructionDate)

  const showModuleProgress = isV2OrHigher(values.dcVersion)
  const courseProgress = calculateV2Progress(values.moduleProgress)
  const moduleProgressLine = formatModuleProgressLine(values.moduleProgress)

  const progressString = !values.dcProgress
    ? ''
    : values.dcProgress.done
    ? 'Done'
    : mapProgressFromObj(values.dcProgress, 'long')

  return (
    <>
      <Box>
        <Flex sx={{ pb: 44 }}>
          <Box>
            <Label>TM App Status</Label>{' '}
            <Text data-cy="userStatus" variant="text.basic">
              {values.appAccountDeleted ? (
                <Tooltip message={APP_DELETED_TOOLTIP} width={280}>
                  <Chip variant="app-account-deleted" />
                </Tooltip>
              ) : (
                <Chip
                  variant={
                    values.hasAppAccess === false
                      ? 'no-app-access'
                      : values.status
                  }
                />
              )}
            </Text>
            {showModuleProgress && moduleProgressLine ? (
              <Box data-cy="userModuleProgress" sx={{ mt: 4, minWidth: 270 }}>
                <Text variant="text.basic" sx={{ display: 'block' }}>
                  Course progress: {courseProgress}%
                </Text>
                <Text variant="text.basic" sx={{ display: 'block', mt: 2 }}>
                  {moduleProgressLine}
                </Text>
              </Box>
            ) : progressString ? (
              <Text data-cy="userProgress" variant="text.basic" sx={{ mt: 2 }}>
                {progressString}
              </Text>
            ) : null}
          </Box>
          {isUserLocked && !disableLockUnlockButton && (
            <UnlockCourseBtn
              disabled={disableLockUnlockButton}
              values={{
                countryCode: values.countryCode,
                userType: values.userType,
                id: values.id,
                instructionDate: initialValues?.instructionDate,
                teachingOrg: values.teachingOrg,
              }}
              isCustomerSupportUser={isSupport}
            />
          )}

          {isUserUnlocked && values.userType !== STUDENT_INPERSON && (
            <LockCourseBtn
              userId={values.id}
              disabled={disableLockUnlockButton}
            />
          )}
        </Flex>
      </Box>
    </>
  )
}
